// The sign-in page's backdrop: the live cube, blurred, turning slowly behind the
// form. It draws the open layer's six faces from GET /api/cube (which cubes are
// dug, and the layer's look: its skin, or its sponsor's picture, as the game
// draws them) onto a cube of six CSS 3D faces, and asks again every 20 seconds
// while the page is showing, so holes open as players dig. Dug cubes show the
// plain ground the game draws under the open layer. Nothing here needs an account.

import { Geometry } from "../shared/geometry.js";
import { LayerState } from "../shared/layer.js";
import { b64ToBytes } from "../shared/protocol.js";
import { skinPlan } from "../shared/skins.js";
import { averageColor, faceCanvas } from "./skins.js";

const EVERY_MS = 20_000;
/** Pixels across a face's picture: blurred, it needs no more. */
const FACE_PX = 384;
// the plain tones the game draws under the open layer (game.js groundUnder): the one further from the layer's own lightness
const GROUND_DARK = [48, 52, 60], GROUND_LIGHT = [172, 177, 186];
/** How much light each face catches, so the cube reads as a solid as it turns: +X, -X, +Y (top), -Y, +Z, -Z. */
const LIGHT = [0.86, 0.74, 1.12, 0.6, 1, 0.8];

/**
 * The cell of layer d (edge L) that column i and row j (from the top) of face
 * f's picture show. Faces follow the game's table: +X, -X, +Y, -Y, +Z, -Z.
 */
function cellOf(f, d, L, i, j) {
  const hi = d + L - 1, up = d + L - 1 - j;
  switch (f) {
    case 0: return [hi, up, hi - i];
    case 1: return [d, up, d + i];
    case 2: return [d + i, hi, d + j];
    case 3: return [d + i, d, hi - j];
    case 4: return [d + i, up, hi];
    default: return [hi - i, up, d];
  }
}

const lum = c => (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;

function loadImage(src) {
  return new Promise((ok, bad) => { const im = new Image(); im.onload = () => ok(im); im.onerror = bad; im.src = src; });
}

/**
 * Starts the backdrop in `host` (an empty element filling the page) and fills
 * `caption` with the layer and the cubes left. Returns a function that stops it.
 */
export function startLoginCube(host, caption) {
  const cube = document.createElement("div");
  cube.className = "cb-cube";
  const faces = Array.from({ length: 6 }, (_, f) => {
    const c = document.createElement("canvas");
    c.className = `cb-face f${f}`;
    c.width = c.height = FACE_PX;
    cube.append(c);
    return c;
  });
  const scene = document.createElement("div");
  scene.className = "cb-scene";
  scene.append(cube);
  host.append(scene);

  // the look of the layer drawn last, kept while it stays the same layer
  let look = null, timer = 0, stopped = false;

  /** Layer d's six faces as pictures, and the ground tone for its holes. */
  async function lookOf(r) {
    const key = `${r.season}:${r.layer.d}:${r.sponsor ? r.sponsor.img : ""}`;
    if (look && look.key === key) return look;
    const pics = [];
    if (r.sponsor) {
      // a sponsor's picture covers every face, on its background colour
      const im = await loadImage(r.sponsor.img);
      for (let f = 0; f < 6; f++) {
        const c = document.createElement("canvas"), g = c.getContext("2d");
        c.width = c.height = FACE_PX;
        g.fillStyle = r.sponsor.bg; g.fillRect(0, 0, FACE_PX, FACE_PX);
        const k = (r.sponsor.fit === "cover" ? Math.max : Math.min)(FACE_PX / im.width, FACE_PX / im.height);
        g.drawImage(im, (FACE_PX - im.width * k) / 2, (FACE_PX - im.height * k) / 2, im.width * k, im.height * k);
        pics.push(c);
      }
    } else {
      const s = skinPlan(r.season, r.layers)[r.layer.d];
      for (let f = 0; f < 6; f++) pics.push(faceCanvas(s.sheet, s.seed, f, FACE_PX));
    }
    const avg = averageColor(pics[4]);
    look = { key, pics, ground: lum(avg) < 0.45 ? GROUND_LIGHT : GROUND_DARK };
    return look;
  }

  async function draw(r) {
    if (!r.layer) { host.classList.remove("on"); caption.hidden = true; return; }
    const geom = new Geometry(r.cubeSize), d = r.layer.d, L = r.cubeSize - 2 * d;
    const state = new LayerState(geom, d);
    state.applyBits(b64ToBytes(r.layer.bits));
    const lk = await lookOf(r), cell = FACE_PX / L, [gr, gg, gb] = lk.ground;
    faces.forEach((c, f) => {
      const g = c.getContext("2d");
      g.drawImage(lk.pics[f], 0, 0, FACE_PX, FACE_PX);
      // the dug cubes: the plain ground, a touch darker in a checker as the game draws it
      for (let j = 0; j < L; j++) for (let i = 0; i < L; i++) {
        const [x, y, z] = cellOf(f, d, L, i, j);
        if (state.isLeftXYZ(x, y, z)) continue;
        const k = (i + j) & 1 ? 1 : 0.9;
        g.fillStyle = `rgb(${gr * k},${gg * k},${gb * k})`;
        g.fillRect(Math.floor(i * cell), Math.floor(j * cell), Math.ceil(cell), Math.ceil(cell));
      }
      c.style.filter = `brightness(${LIGHT[f]})`;
    });
    host.classList.add("on");
    const fmt = new Intl.NumberFormat("en-GB");
    caption.lastChild.textContent = `Live: layer ${r.layer.no} of ${r.layers}, ${fmt.format(r.layer.left)} block${r.layer.left === 1 ? "" : "s"} left`;
    caption.hidden = false;
  }

  async function tick() {
    clearTimeout(timer);
    if (stopped) return;
    try {
      const res = await fetch("/api/cube", { credentials: "same-origin" });
      if (res.ok) await draw(await res.json());
    } catch (_) { /* the server is restarting, or the picture didn't load: the page works as it is */ }
    if (!stopped && !document.hidden) timer = setTimeout(tick, EVERY_MS);
  }
  // only while the page is showing; back on it, catch up at once
  const onVis = () => { if (!document.hidden) tick(); else clearTimeout(timer); };
  document.addEventListener("visibilitychange", onVis);
  tick();
  return () => { stopped = true; clearTimeout(timer); document.removeEventListener("visibilitychange", onVis); };
}
